// src/pages/More/ProjectorYear.jsx
import React, { useEffect, useState, Fragment  } from 'react';
import './../Playoff.css';
import RunSim from './components/runsim';
import { Settings } from 'lucide-react';

import { listOfTeams } from './teams/team';
import { championshipGames } from './components/champGames';
import { processSimulationData } from './components/processSimulationData';
import LeaderboardTable from './components/LeaderboardTable';
import PlayoffProjection from './components/PlayoffProjection';

import { resolveACCTie } from './components/Ties/ACC';
import { resolveSECTie } from './components/Ties/SEC';
import { resolveBig12Ties } from './components/Ties/B12';
import { resolveBigTenTies } from './components/Ties/B10';
import { resolveMACTie } from './components/Ties/MAC';
import { resolveG5Tie } from './components/Ties/G5';
import { resolveAmericanTie } from './components/Ties/American';
import { resolveCUSATie } from './components/Ties/CUSA';
import { resolveMWTie } from './components/Ties/MountainWest';
import { resolvePac12Tie } from './components/Ties/Pac12';
import { resolveSunBeltDivision, resolveSunBeltTie } from './components/Ties/SunBelt';
import { Link } from 'react-router-dom';


// ============================================================================
// UNIVERSAL CHAMPIONSHIP RESOLVER (parameterized)
// ============================================================================
const updateChampionshipGames = (teams, champGames, manualGames = [], allGames = []) => {



    const ssarankings = Object.fromEntries(
      teams.map(t => [t.team, t.avg])
    );

    const options = {
            ssarankings,
            ssrHigherIsBetter: false,
        }


    return champGames.map(game => {

        const id = String(game?.notes || "");

        const confTeams = teams.filter(t => t.conference === game.conference);

        // SUN BELT SPECIAL
        if (game.conference === "Sun Belt") {
            const DIV = {
                "Appalachian State": "East",
                "Coastal Carolina": "East",
                "Georgia Southern": "East",
                "Georgia State": "East",
                "James Madison": "East",
                "Marshall": "East",
                "Old Dominion": "East",
                "Arkansas State": "West",
                "Louisiana": "West",
                "Louisiana-Monroe": "West",
                "South Alabama": "West",
                "Southern Miss": "West",
                "Texas State": "West",
                "Troy": "West",
            };

            const east = confTeams.filter(t => DIV[t.team] === "East");
            const west = confTeams.filter(t => DIV[t.team] === "West");

            const resolveDivision = division => {
                const maxWins = Math.max(...division.map(t => Number(t.conferenceWins)));
                const leaders = division.filter(t => Number(t.conferenceWins) === maxWins);
                const clones = leaders.map(t => ({ ...t }));
                return resolveSunBeltDivision(clones, confTeams, allGames, options)[0];
            };

            const eastWinner = resolveDivision(east);
            const westWinner = resolveDivision(west);

            const pair = [eastWinner.team, westWinner.team].sort();
            let updatedGame = { ...game, homeTeam: pair[0], awayTeam: pair[1] };

            return updatedGame;
        }

        // NON–SUN BELT
        const groups = confTeams.reduce((m, t) => {
            const k = Number(t.conferenceWins);
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(t);
            return m;
        }, new Map());


        const wins = confTeams.map(t => Number(t.conferenceWins));
        const maxWins = Math.max(...wins);
        const minWins = Math.min(...wins);

        

        function callResolver(group) {
            const clones = group.map(t => ({ ...t }));
            if(game.conference == "ACC"){
                //console.log(clones);
            }
            if (clones.length <= 1) return clones;

            const c = game.conference;
            if (c === "ACC") return resolveACCTie(clones, confTeams, allGames, options);
            if (c === "SEC") return resolveSECTie(clones, confTeams, allGames, options);
            if (c === "Big 12") return resolveBig12Ties(clones, confTeams, allGames, options);
            if (c === "Big Ten") {
                //console.log(clones);
                //console.log(allGames.filter(g => g.homeTeam === "Indiana" && g.awayTeam === "Ohio State"));
                return resolveBigTenTies(clones, confTeams, allGames, options);
                }
if (c === "Mid-American") return resolveMACTie(clones, confTeams, allGames, options);
            if (c === "American Athletic") return resolveAmericanTie(clones, confTeams, allGames, options);
            if (c === "Conference USA") return resolveCUSATie(clones, confTeams, allGames, options);
            if (c === "Mountain West") return resolveMWTie(clones, confTeams, allGames, options);
            if (c === "Pac-12") return resolvePac12Tie(clones, confTeams, allGames, options);
            if (c === "Sun Belt") return resolveSunBeltTie(clones, confTeams, allGames, options);
            return resolveG5Tie(clones, confTeams, allGames);
        }

        let sortedGroups = [];

        for (let w = maxWins; w >= minWins; w--) {
            const group = groups.get(w) || [];
            if (!group.length) continue;

            let resolved = callResolver(group);
            
            if (!Array.isArray(resolved)) resolved = [resolved];
            resolved = resolved.flat().filter(t => t?.team);
            if (resolved.length) sortedGroups.push(resolved);
        }

        const flattened = sortedGroups.flat();

        if (flattened[0].conference == "ACC") {
        }
        

        let updatedGame = game;

        if (flattened.length >= 2) {
            const topTwo = [flattened[0].team, flattened[1].team].sort();
            updatedGame = { ...game, homeTeam: topTwo[0], awayTeam: topTwo[1] };
        }

        const manualGame = manualGames.find(
            game =>
                (game.homeTeam === updatedGame.homeTeam &&
                 game.awayTeam === updatedGame.awayTeam) ||
                (game.homeTeam === updatedGame.awayTeam &&
                 game.awayTeam === updatedGame.homeTeam)
        );

        if (manualGame) {
            return manualGame;
        }

        return updatedGame;
    });
};


// ============================================================================
// MAIN UNIFIED COMPONENT
// ============================================================================
const ProjectorYear = ({
    year,
    initialWeek,
    teamRatings,
    gamesData,
    ratingModel,
}) => {

    const [teams, setTeams] = useState([]);
    const [games, setGames] = useState([]);
    const [manualGames, setManualGames] = useState([]);
    const [ratingsData, setRatingsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [week, setWeek] = useState(initialWeek);
    const [rngSeed, setRngSeed] = useState("test");
    const [oddsFactor, setOddsFactor] = useState(false);
    const [mode, setMode] = useState('light');
    const [tab, setTab] = useState('bracket');
    const [logo_type, set_logo_type] = useState('');
    const [mSwitch, setMSwitch] = useState(false);

    

    const alwaysHalf = () => 0.5;
    const seededRandom = seed => {
        let m = 0x80000000, a = 1103515245, c = 12345;
        let state = seed;
        return () => {
            state = (a * state + c) % m;
            return state / (m - 1);
        };
    };

    useEffect(() => {
        setWeek(initialWeek+1);
        }, [year])

    // ============================================================================
    // INITIAL LOAD
    // ============================================================================
    useEffect(() => {
        const load = async () => {
            setLoading(true);

            try {

                teamRatings.sort((a, b) => {
                    if (a.team < b.team) return -1;
                    if (a.team > b.team) return 1;
                    return 0; // equal
                });

                setRatingsData(teamRatings);
                const seed = Math.floor(Math.random() * 0x7fffffff);
                setRngSeed(seed);

                const rand = oddsFactor ? seededRandom(seed) : alwaysHalf;

                const { updatedTeams, processedGames } = processSimulationData(teamRatings, gamesData, week, rand, ratingModel, false, year, false);
                const champGames = updateChampionshipGames(updatedTeams, championshipGames, [], processedGames);
                const merged = [...processedGames, ...champGames];
                const { updatedTeams: finalTeams, processedGames: finalGames } = processSimulationData(teamRatings, merged, week, rand, ratingModel, false, year, false);

                setTeams(finalTeams);
                setGames(finalGames);
                setManualGames(finalGames);
            } catch (err) {
                console.error(err);
            }

            setLoading(false);
        };

        load();
    }, [year, oddsFactor]);

    // ============================================================================
    // GAME CHANGE HANDLER (identical across years)
    // ============================================================================
    const handleGameChange = (updatedGame, mg = manualGames, rd = ratingsData, w = week) => {

        const isChampGame = updatedGame?.id?.toString()?.toLowerCase().includes("championship");
        let seed = isChampGame && rngSeed ? rngSeed : Math.floor(Math.random() * 0x7fffffff);
        const rand = oddsFactor ? seededRandom(seed) : alwaysHalf;

        if (updatedGame == null) {
            const { updatedTeams } = processSimulationData(rd, mg, w, rand, ratingModel, true, year, false);

            setManualGames(mg);
            setTeams(updatedTeams);
        }
        else
        {

            const newManual = mg.map(g => (updatedGame && g.id === updatedGame.id)
                ? { ...g, ...updatedGame }
                : g
            );

            
            if (!isChampGame) setRngSeed(seed);

            

            const regular = newManual.filter(g => {
                const id = String(g?.id || "");   // safely convert to string
                const notes = String(g?.notes || "");
                return !id.toLowerCase().includes("championship") && !notes.toLowerCase().includes("championship");
            });

            const champs = newManual.filter(g => {
                const id = String(g?.id || "");   // safely convert to string
                const notes = String(g?.notes || "");   // safely convert to string
                return (id.toLowerCase().includes("championship") || notes.toLowerCase().includes("championship"));
            });


            const oldchamps = newManual.filter(g => {
                const id = String(g?.id || "");   // safely convert to string
                                const notes = String(g?.notes || "");

                return (id.toLowerCase().includes("championship") || notes.toLowerCase().includes("championship"));
            });


            // Only preserve manual championship edits if the edit was to a championship game itself
            // For regular season game edits, start fresh with template championship games
            const manualChamps = champs.filter(g => g.manual === true);

            const { updatedTeams } = processSimulationData(rd, regular, w, rand, ratingModel, true, year, false);

            // Always use the template championshipGames for determining tiebreakers - never use manually-edited outcomes
            // Start fresh from the template to ensure stale championship games don't linger
            const champUpdated = updateChampionshipGames(updatedTeams, championshipGames, manualChamps, regular);

            // Apply any manual edits to the calculated championship games
            const champWithManualEdits = champUpdated.map(game => {
                const manualEdit = manualChamps.find(g => g.id === game.id);

                //if (manualEdit) return { ...game, ...manualEdit, manual: true };

                // If there was a previous championship game with the same matchup, preserve its result
                const prev = oldchamps.find(g => (
                    (g.homeTeam === game.homeTeam && g.awayTeam === game.awayTeam) ||
                    (g.homeTeam === game.awayTeam && g.awayTeam === game.homeTeam)
                ));

                if (prev && (prev.homePoints !== undefined || prev.awayPoints !== undefined || prev.completed)) {
                    return {
                        ...game,
                        homePoints: prev.homePoints,
                        awayPoints: prev.awayPoints,
                        completed: !!prev.completed,
                        manual: !!prev.manual ||  game.year < 2026,
                        // preserve winner if present
                        winner: prev.winner || (prev.homePoints > prev.awayPoints ? prev.homeTeam : prev.awayTeam)
                    };
                }

                return game;
            });

            const merged = [...regular, ...champWithManualEdits];

            const { updatedTeams: finalTeams, processedGames: finalGames } = processSimulationData(rd, merged, w, rand, ratingModel, true, year, false);

            setManualGames(finalGames);
            setTeams(finalTeams);
            setGames(finalGames);
        }
    };

    const yearMenu = [2026, 2025, 2024, 2023, 2022, 2021];

    const YearMenu = ({ year }) => (
    <div>
        {yearMenu.map((y, i) => (
            <Fragment key={y}>
                {y === year ? (
                    <b>{y}</b>
                ) : (
                    <Link to={`/projection/${y}`}>{y}</Link>
                )}
                {i < yearMenu.length - 1 && ' | '}
            </Fragment>
        ))}
    </div>
);

    // ============================================================================
    // RENDER
    // ============================================================================


    const simResults = year == 2026 ? RunSim(manualGames) : RunSim([]);
    const percent = simResults.percent;
    const records = simResults.records;
    const grades = simResults.grades;
    const avgPositions = simResults.avgPositions;


  
    const dark = false;

    return (
        <>
            <div className="header special-header">
                <div className="row">
                    <img src="../cfp-w.png" alt="CFBP Logo" className="cfbp-logo" />
                    <h1>{year} College Football Playoff Projection</h1>
                </div>

                {year == 2026 && <h3 className="">Updated Oct. 12, 2026</h3>}
            </div>
            <div className={`AppGameList leaderboard playoff-projection-container ppc ${mode} year-${year}`}>





            {!loading ? (
                <>
                    
                    
                    
                    <div className="leaderboard-table cfp">
</div>
                    {(tab == "table" || mSwitch) && <LeaderboardTable
                        year={year}
                        week={week}
                        setWeek={setWeek}
                        games={manualGames}
                        onGameChange={handleGameChange}
                        teams={teams}
                        listOfTeams={listOfTeams}
                        percent={percent}
                        records={records}
                        grades={grades}
                        avgPositions={avgPositions}
                        simResults={simResults}
                        dark={mode == "dark"}
                        logo_type={logo_type}
                        set_logo_type={set_logo_type}
                        setOddsFactor={setOddsFactor}
                        mSwitch={mSwitch}
                        setMSwitch={setMSwitch}
                    />}

                    {(tab == "bracket" && !mSwitch) && <PlayoffProjection
                        teams={teams}
                        grades={grades}
                        records={records}
                        listOfTeams={listOfTeams}
                        final={week > 15}
                        year={year}
                        dark={mode == "dark"}
                        logo_type={logo_type}
                        set_logo_type={set_logo_type}
                        YearMenu={YearMenu}
                        loading={simResults.loading}
                        simResults={simResults}
                        mSwitch={mSwitch}
                        setMSwitch={setMSwitch}
                    />}

                    {!mSwitch && (
                            <div id="scenario-menu">
                                {!mSwitch && (
                                    <>
                                        <div className="tab-group">
                                            <div className={`tab left ${tab == "bracket" && "active"}`} onClick={() => setTab("bracket")}>Bracket</div>

                                            <div className={`tab right ${tab == "table" && "active"}`} onClick={() => setTab("table")} >Teams</div>


                                        </div>
                                    </>
                                )}  
                                {year === 2026 && (
                                    !simResults.loading ? (
                                        <div className="slim-butt" onClick={() => {setMSwitch(true); setWeek(14);}}>
                                            <Settings size={24} strokeWidth={2} />
                                            &nbsp; Scenario Editor
                                        </div>
                                    ) : (
                                        <div className="fixed-mid">
                                            Simulating... ({simResults.numSimulations * 4}/1000)
                                        </div>
                                    )
                                )}
                            </div>

)}                </>
            ) : (
                <div>Refreshing...</div>
                )}


            <style dangerouslySetInnerHTML={{
                __html: `
    .manual-game-editor { margin-top: 20px; }
    .loss-img { padding: 1px; border: 2px solid transparent; border-radius: 0; }
    body { max-width: 600px; margin: auto; }
    html { max-width: 100%; position:relative; left:auto; transform:none; }
    .manual-game-table { width: 100%; border-collapse: collapse; text-align: center; }
    .manual-game-table th, .manual-game-table td { padding: 10px; vertical-align: middle; }
    .manual-game-table td { width: 45%; }
    .manual-game-table .versus { width: 10%; font-weight: bold; text-align:center; }
    .clickable-team { display: flex; align-items: center; justify-content: flex-start; cursor: pointer; padding: 5px 10px; border-radius: 5px; transition: background 0.2s; }
    .clickable-team img.team-logo { width: 24px; height: 24px; margin-right: 6px; }
    .clickable-team.selected { background-color: #eee; font-weight: bold; color:#333; }
    .clickable-team.selected.manual { background-color: #aaa; font-weight: bold; color:black; }
    .manual-game-table td:last-child .clickable-team img.team-logo { margin-left: 6px; margin-right: 0; }
    #root{margin:0; max-width:100%;}
    .leaderboard .manual-game-table th{text-align:center !important;}
    .clickable-team.away{flex-direction: row-reverse;}
  `
            }} />
            </div>

        </>
)   }                


export default ProjectorYear;
